import LoginLayout from '@/components/layouts/LoginLayout'
import { Form, Head } from '@inertiajs/react'
import { Input, Button } from 'antd'
import { useState, type ChangeEvent, type ClipboardEvent } from 'react'

interface OtpVerifyProps {
  csrf_token: string
  email?: string
  otp_preview?: string | null
}

export default function OtpVerify({ csrf_token, email, otp_preview }: OtpVerifyProps) {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const isDev = import.meta.env.DEV

  const maskedEmail = email ? `${email.split('@')[0].slice(0, 2)}***@${email.split('@')[1]}` : null

  const normalizeCode = (value: string) => value.replace(/\D/g, '').slice(0, 6)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(normalizeCode(event.target.value))
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('Text')
    if (!pasted) return

    setCode(normalizeCode(pasted))
  }

  const showOtpPreview = Boolean(isDev && otp_preview)

  return (
    <LoginLayout title="Verify your OTP">
      <Head title="Verify OTP" />
      <div className="w-[500px] bg-white shadow-md p-14">
        <div className="w-[388px] mx-auto">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
            Enter the 6-digit code
          </h2>
          <p className="text-sm text-gray-600 text-center mb-6">
            {maskedEmail
              ? `We sent an OTP to ${maskedEmail}. Enter it below to continue.`
              : 'Check your email for the OTP we just sent and enter it below.'}
          </p>
          {showOtpPreview && (
            <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-900">
              <span className="font-semibold">Dev OTP:</span> <span className="tracking-[0.5em]">{otp_preview}</span>
            </div>
          )}
          <Form
            action="/login/otp/verify"
            method="post"
            onSubmit={() => setLoading(true)}
            onSuccess={() => setLoading(false)}
            onError={() => setLoading(false)}
          >
            <input type="hidden" name="authenticity_token" value={csrf_token} />
            <div className="mb-5">
              <label htmlFor="otp-code" className="block text-sm font-medium text-gray-700 mb-1">
                6-digit OTP
              </label>
              <Input
                id="otp-code"
                name="otp[code]"
                value={code}
                maxLength={6}
                onChange={handleChange}
                onPaste={handlePaste}
                className="ant-input w-full tracking-[0.4em] text-center text-sm"
                placeholder="••••••"
                inputMode="numeric"
                autoComplete="one-time-code"
                // pattern="\\d{6}"
                title="Enter the 6-digit code"
                required
              />
            </div>
            <div className="mt-6">
              <Button
                type="primary"
                htmlType="submit"
                className="w-full text-white border-none rounded font-medium cursor-pointer mt-2 block"
                loading={loading}
              >
                Verify and Sign in
              </Button>
            </div>
          </Form>
          <div className="mt-4 text-center space-y-2">
            <a className="text-blue-500 text-sm block" href="/login/otp">
              Resend or use a different email
            </a>
            <a className="text-blue-500 text-sm block" href="/login">
              Back to password login
            </a>
          </div>
        </div>
      </div>
    </LoginLayout>
  )
}
